import { GzPolicyUiSettingsGridItemFormWebComponent } from '../../../../../../common/policyPanel/uiSettings/gridTemplate/form';
import { html, TemplateResult } from 'lit';
import { translateFn } from '../../../../../../../utils/translator.helper';
import {
  getExclusionItemTypeFormOptions,
  getStorageExclusionsGridItemSearchableKeywords,
} from '../../../../utils/uiSettings/storageProtection/storage-protection.values';
import { StorageExclusionsSearchableKeywords } from '../../../../utils/uiSettings/storageProtection/storage-protection.types';
import { RequiredValidator } from '../../../../../../../ui/gzForm/validators/required.validator';
import { CustomizableValidator } from '../../../../../../../ui/gzForm/validators/customizable.validator';
import { validateHashesIfNeeded } from '../utils/validators';
import { customElement, query } from 'lit/decorators.js';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzDropdownWebComponent } from '../../../../../../../ui/gzDropdown';
import { GzInputWebComponent } from '../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { WEB_COMPONENT_POLICY_PANEL_STORAGE_PROTECTION_EXCLUSION_ITEM } from '../../../../../../../webcomponents.constants';
import { StorageExclusionItemType } from '@nirvana/shared/dtos/types/policies/common';

@customElement(WEB_COMPONENT_POLICY_PANEL_STORAGE_PROTECTION_EXCLUSION_ITEM)
export class GzStorageProtectionExclusionItemFormWebComponent extends GzPolicyUiSettingsGridItemFormWebComponent {
  protected searchableKeywords = getStorageExclusionsGridItemSearchableKeywords();

  @query('#excludedItemType')
  excludedItemType: GzFormFieldWebComponent<GzDropdownWebComponent>;

  @query('#excludedItemPath')
  excludedItemPath: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('#excludedItemDescription')
  excludedItemDescription: GzFormFieldWebComponent<GzInputWebComponent, string>;

  private translations = {
    hashTextInfo: translateFn('Add SHA256 hashes separated by comma'),
    wildcardTextInfo: translateFn(
      'Specify file path (example: /folder/file1.txt) or use * and ? as wildcards (example: */file1.txt)'
    ),
  };

  public setError(error: string) {
    this.excludedItemPath.setError(error);
  }

  renderFormFields(): TemplateResult {
    return html`
      <div class="policy-grid-item-form--field excluded-item-type">
        <gz-form-field id="excludedItemType">
          <gz-dropdown
            @selectionChanged="${this.handleTypeFieldChange}"
            gz-form-input
            label="${translateFn('Type')}"
            options="${JSON.stringify(getExclusionItemTypeFormOptions())}"
            .value="${StorageExclusionItemType.HASH}"
            searchable-label="${this.searchableKeywords[StorageExclusionsSearchableKeywords.StorageHash].label} ${this
              .searchableKeywords[StorageExclusionsSearchableKeywords.StorageWildcard].label}"
          ></gz-dropdown>
        </gz-form-field>
      </div>

      <div class="policy-grid-item-form--field excluded-item-path input--w40">
        <gz-form-field id="excludedItemPath">
          <gz-input
            gz-form-input
            label="${translateFn('Path')}"
            placeholder="${this.translations.hashTextInfo}"
          ></gz-input>
        </gz-form-field>
      </div>

      <div class="policy-grid-item-form--field excluded-item-description input--w40">
        <gz-form-field id="excludedItemDescription">
          <gz-input
            gz-form-input
            label="${translateFn('Description')}"
            placeholder="${translateFn('Add description')}"
          ></gz-input>
        </gz-form-field>
      </div>
    `;
  }

  createFormGroup(): void {
    this.excludedItemType.Validators = [RequiredValidator('')];
    this.excludedItemPath.Validators = [
      RequiredValidator(translateFn('Required field')),
      CustomizableValidator(value =>
        validateHashesIfNeeded(this.excludedItemType.value as StorageExclusionItemType, value)
      ),
    ];

    this.formGroup = new FormGroup({
      type: this.excludedItemType,
      path: this.excludedItemPath,
      description: this.excludedItemDescription,
    });
  }

  private handleTypeFieldChange(event: CustomEvent) {
    switch (event.detail.value) {
      case StorageExclusionItemType.HASH:
        this.excludedItemPath.Input.placeholder = this.translations.hashTextInfo;
        break;
      case StorageExclusionItemType.WILDCARD:
        this.excludedItemPath.Input.placeholder = this.translations.wildcardTextInfo;
        break;
      default:
        break;
    }

    this.excludedItemPath.validate();
  }

  resetForm(resetType = true): void {
    if (resetType) {
      this.excludedItemType.value = StorageExclusionItemType.HASH;
      this.excludedItemPath.Input.placeholder = this.translations.hashTextInfo;
    }

    this.excludedItemPath.value = '';
    this.excludedItemDescription.value = '';

    this.excludedItemType.resetField();
    this.excludedItemPath.resetField();
    this.excludedItemDescription.resetField();
  }

  reset() {
    this.resetForm();
  }

  resetOnAdd() {
    this.resetForm(false);
  }
}
